import { useCallback, useRef } from 'react'
import { proxy, useSnapshot } from 'valtio'
import {
  getLocalAwarenessState,
  setLocalAwarenessField,
  useAwareness,
  isAwarenessSyncDisabled,
  type AwarenessState,
  type NodeTransientState,
} from '@/shared/lib/yjs/awareness'

// 纯本地的节点瞬态存储（不会通过网络同步），专门给个人模式或者需要严格隔离本地状态的场景使用
export const localNodeTransientStore = proxy<{ ns: Record<string, NodeTransientState> }>({ ns: {} })

export function setLocalNodeTransientState(nodeId: string, patch: Partial<NodeTransientState>): void {
  if (!localNodeTransientStore.ns[nodeId]) {
    localNodeTransientStore.ns[nodeId] = {}
  }
  Object.assign(localNodeTransientStore.ns[nodeId], patch)
}

export function setNodeAwareness(
  nodeId: string,
  patch: Partial<NodeTransientState>,
): void {
  // 1. 始终保存到纯本地 store，保证本地 UI 能立刻、独立地响应
  setLocalNodeTransientState(nodeId, patch)

  // 2. 如果开启了协同，才走到 Yjs awareness 网络广播
  if (!isAwarenessSyncDisabled) {
    const current = getLocalAwarenessState()
    const currentNodeStates = current.ns ?? {}
    const currentNodeState = currentNodeStates[nodeId] ?? {}

    setLocalAwarenessField('ns', {
      ...currentNodeStates,
      [nodeId]: { ...currentNodeState, ...patch },
    })
  }
}

export function setNodeUploading(nodeId: string, isUploading: boolean): void {
  setNodeAwareness(nodeId, { iu: isUploading })
}

export function setNodeEditing(nodeId: string, isEditing: boolean): void {
  setNodeAwareness(nodeId, { ie: isEditing })
}

export function clearNodeAwareness(nodeId: string): void {
  delete localNodeTransientStore.ns[nodeId]

  if (!isAwarenessSyncDisabled) {
    const current = getLocalAwarenessState()
    const currentNodeStates = { ...(current.ns ?? {}) }
    delete currentNodeStates[nodeId]
    setLocalAwarenessField('ns', currentNodeStates)
  }
}

export interface NodeAwarenessState {
  isUploading?: boolean
  isEditing?: boolean
  editorUid?: string
  editorClientId?: number
}

let lastSelectedNodeIds: string[] = []

export function syncLocalSelectionToAwareness(selectedNodeIds: string[]): void {
  const selectedSet = new Set(selectedNodeIds)
  const lastSelectedSet = new Set(lastSelectedNodeIds)

  // 更新本地独立状态
  for (const nodeId of lastSelectedNodeIds) {
    if (!selectedSet.has(nodeId)) {
      setLocalNodeTransientState(nodeId, { ie: false })
    }
  }
  for (const nodeId of selectedNodeIds) {
    if (!lastSelectedSet.has(nodeId)) {
      setLocalNodeTransientState(nodeId, { ie: true })
    }
  }

  // 同步到网络协同（如果开启）
  if (!isAwarenessSyncDisabled) {
    const current = getLocalAwarenessState()
    const currentNodeStates = current.ns ?? {}
    
    let changed = false
    const nextStates = { ...currentNodeStates }

    for (const nodeId of lastSelectedNodeIds) {
      if (!selectedSet.has(nodeId) && nextStates[nodeId]?.ie) {
        nextStates[nodeId] = { ...nextStates[nodeId], ie: false }
        changed = true
      }
    }

    for (const nodeId of selectedNodeIds) {
      if (!lastSelectedSet.has(nodeId) && !nextStates[nodeId]?.ie) {
        nextStates[nodeId] = { ...nextStates[nodeId], ie: true }
        changed = true
      }
    }

    if (changed) {
      setLocalAwarenessField('ns', nextStates)
    }
  }

  // 更新缓存
  lastSelectedNodeIds = selectedNodeIds
}


export function useNodeAwareness(nodeId: string): NodeAwarenessState {
  const nodeIdRef = useRef(nodeId)
  nodeIdRef.current = nodeId

  const remoteCachedRef = useRef<NodeAwarenessState>({ isUploading: false, isEditing: false })
  const finalCachedRef = useRef<NodeAwarenessState>({ isUploading: false, isEditing: false })

  const selector = useCallback(
    (states: Map<number, AwarenessState>): NodeAwarenessState => {
      let isUploading = false
      let isEditing = false
      let editorUid: string | undefined
      let editorClientId: number | undefined

      for (const [clientId, state] of states) {
        const nodeState = state?.ns?.[nodeIdRef.current]
        if (nodeState?.iu) {
          isUploading = true
        }
        if (nodeState?.ie) {
          isEditing = true
          if (state.u) {
            editorUid = state.u
            editorClientId = clientId
          }
        }
      }

      if (
        remoteCachedRef.current.isUploading === isUploading &&
        remoteCachedRef.current.isEditing === isEditing &&
        remoteCachedRef.current.editorUid === editorUid &&
        remoteCachedRef.current.editorClientId === editorClientId
      ) {
        return remoteCachedRef.current
      }

      remoteCachedRef.current = { isUploading, isEditing, editorUid, editorClientId }
      return remoteCachedRef.current
    },
    [],
  )

  const remoteAwareness = useAwareness(selector)
  
  // 订阅本地独立的瞬态 Store
  const localNs = useSnapshot(localNodeTransientStore).ns[nodeId]

  const finalIsUploading = remoteAwareness.isUploading || !!localNs?.iu
  const finalIsEditing = remoteAwareness.isEditing || !!localNs?.ie

  if (
    finalCachedRef.current.isUploading === finalIsUploading &&
    finalCachedRef.current.isEditing === finalIsEditing &&
    finalCachedRef.current.editorUid === remoteAwareness.editorUid &&
    finalCachedRef.current.editorClientId === remoteAwareness.editorClientId
  ) {
    return finalCachedRef.current
  }

  finalCachedRef.current = {
    isUploading: finalIsUploading,
    isEditing: finalIsEditing,
    editorUid: remoteAwareness.editorUid,
    editorClientId: remoteAwareness.editorClientId,
  }

  return finalCachedRef.current
}
